<template>
  <v-container fluid class="legal-page pa-0">
    <section class="hero d-flex align-center justify-center">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="9" lg="8">
            <v-card class="legal-card pa-6 pa-md-8">
              <div class="text-center mb-7">
                <v-img
                  :src="require('@/assets/logo.png')"
                  contain
                  height="90"
                  class="mx-auto mb-4"
                />
                <h1 class="font-weight-bold">{{ $t('info.legalTitle') }} - Konvenio</h1>
                <p class="legal-date mb-0">
                  Fecha de entrada en vigor: 4 de mayo de 2026 |
                  Última actualización: 4 de mayo de 2026 |
                  Versión: v{{ legalTermsVersion }}
                </p>
              </div>

              <div class="legal-text">
                <p>
                  De conformidad con lo dispuesto en la Ley 34/2002, de Servicios de la
                  Sociedad de la Información y del Comercio Electrónico —LSSI-CE—, se
                  informa a los usuarios de los siguientes datos:
                </p>

                <section v-for="section in sections" :key="section.title" class="legal-section">
                  <h2>{{ section.title }}</h2>
                  <p v-for="paragraph in section.paragraphs" :key="paragraph">
                    {{ paragraph }}
                  </p>

                  <ul v-if="section.items" class="legal-list">
                    <li v-for="item in section.items" :key="item" v-html="item"></li>
                  </ul>
                </section>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </v-container>
</template>

<script>
import { LEGAL_TERMS_VERSION } from '@/config/legal'

export default {
  name: 'LegalNotice',
  computed: {
    legalTermsVersion() {
      return LEGAL_TERMS_VERSION
    }
  },
  data() {
    return {
      sections: [
        {
          title: '1. Identificación del Responsable',
          paragraphs: [],
          items: [
            '<strong>Titular:</strong> Sorraylla Ramos',
            '<strong>Correo electrónico de contacto:</strong> <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>',
            '<strong>Sitio web:</strong> <a href="https://konvenio.thevalkiria.com/" target="_blank" rel="noopener">https://konvenio.thevalkiria.com/</a>',
            '<strong>País:</strong> España'
          ]
        },
        {
          title: '2. Objeto',
          paragraphs: [
            'Konvenio es una aplicación y servicio digital destinado a la gestión y consulta de información relacionada con convenios, avisos, documentos, calendarios laborales, trabajadores y comunicaciones internas de una empresa.',
            'El servicio cuenta con una parte web destinada al gestor o administrador y una aplicación destinada a trabajadores, principalmente para consulta de información publicada por la empresa o gestor.'
          ]
        },
        {
          title: '3. Condiciones de Uso',
          paragraphs: [
            'El acceso y uso de Konvenio atribuye la condición de usuario e implica la aceptación del presente Aviso Legal y de las demás condiciones, políticas o avisos aplicables.',
            'El usuario se compromete a hacer un uso adecuado del servicio y a no emplearlo para actividades ilícitas, contrarias a la buena fe, al orden público, a los derechos de terceros o a la normativa vigente.',
            'El usuario deberá respetar en todo momento las normas de conducta y usos prohibidos establecidos en este Aviso Legal.'
          ]
        },
        {
          title: '4. Registro, Acceso y Roles',
          paragraphs: [
            'El uso de determinadas funciones requiere registro o inicio de sesión mediante Google, email y contraseña u otros métodos disponibles a través de Firebase Authentication.',
            'Konvenio puede diferenciar entre gestores o administradores de empresa y trabajadores. El gestor puede administrar información de la empresa, subir documentos, publicar avisos, actualizar calendarios laborales y aceptar o rechazar trabajadores asociados a la empresa.',
            'El trabajador podrá consultar la información correspondiente a la empresa a la que pertenezca o haya sido aceptado, según los permisos y funciones disponibles.'
          ]
        },
        {
          title: '5. Uso Prohibido y Normas de Conducta',
          paragraphs: [
            'El usuario se compromete a utilizar Konvenio de forma lícita, responsable y conforme a la normativa vigente, a la buena fe, al orden público y a los derechos de terceros.',
            'Queda expresamente prohibido el uso de Konvenio para:'
          ],
          items: [
            'Publicar, compartir, almacenar o difundir contenido violento, sexual, pornográfico o que incluya desnudos explícitos.',
            'Promover, fomentar o justificar el odio, la discriminación, el acoso, la intimidación o la violencia por razón de raza, sexo, religión, orientación sexual, identidad de género, discapacidad, nacionalidad, origen étnico, edad u otras circunstancias personales o sociales.',
            'Compartir contenido ilegal, ofensivo, difamatorio, amenazante, abusivo o que infrinja derechos de terceros.',
            'Introducir, compartir o tratar datos personales de terceros sin autorización o sin una base legal suficiente.',
            'Introducir datos sensibles, como información de salud, ideología, religión, orientación sexual, datos biométricos o similares, salvo que exista autorización suficiente y una base legal válida.',
            'Subir documentos, contratos, acuerdos, convenios o comunicaciones que infrinjan derechos de terceros o contengan información innecesaria, excesiva o ilícita.',
            'Suplantar la identidad de otras personas, empresas, entidades o usuarios.',
            'Utilizar la aplicación con fines fraudulentos, abusivos, engañosos o contrarios a la buena fe.',
            'Introducir malware, virus, código dañino, spam, automatizaciones abusivas o cualquier elemento que pueda dañar la aplicación, sus sistemas o a otros usuarios.',
            'Intentar acceder sin autorización a cuentas, datos, empresas, trabajadores, documentos, calendarios, sistemas o áreas restringidas de Konvenio.',
            'Interferir, sobrecargar, bloquear, alterar o perjudicar el funcionamiento normal del servicio.',
            'Usar Konvenio de forma contraria a las políticas de Google Play u otras plataformas de distribución aplicables.',
            'Utilizar Konvenio de cualquier forma contraria a la legislación aplicable.',
            'El incumplimiento de estas normas podrá dar lugar a la suspensión, limitación o eliminación de la cuenta del usuario, así como a la eliminación de contenidos, documentos, avisos, comentarios o datos que incumplan estas condiciones, sin perjuicio de las acciones legales que pudieran corresponder.'
          ]
        },
        {
          title: '6. Responsabilidad del Usuario',
          paragraphs: [
            'El usuario es responsable de la información que introduzca, suba, publique, consulte o comparta a través de Konvenio.',
            'El gestor o administrador de empresa es responsable de comprobar que dispone de autorización o base legal suficiente para subir documentos, datos de empresa, datos de trabajadores, avisos, calendarios laborales, contratos, acuerdos, convenios o cualquier otra información incorporada al servicio.',
            'El usuario deberá custodiar sus credenciales de acceso y notificar cualquier uso no autorizado de su cuenta.'
          ]
        },
        {
          title: '7. Responsabilidad de Konvenio',
          paragraphs: [
            'El titular no se hace responsable de posibles errores técnicos, interrupciones del servicio, pérdida de disponibilidad, fallos de conectividad, incidencias de terceros proveedores o daños derivados del uso de la aplicación, aunque se compromete a realizar esfuerzos razonables para garantizar su correcto funcionamiento.',
            'Konvenio no garantiza que el servicio esté disponible de forma ininterrumpida o libre de errores en todo momento.'
          ]
        },
        {
          title: '8. Disponibilidad del Servicio',
          paragraphs: [
            'El servicio puede ser modificado, suspendido o interrumpido por razones técnicas, de mantenimiento, seguridad, cambios funcionales, necesidades operativas o causas ajenas al titular.'
          ]
        },
        {
          title: '9. Propiedad Intelectual e Industrial',
          paragraphs: [
            'Todos los contenidos, diseño, código, textos, logotipos, marcas, elementos gráficos y componentes propios de Konvenio son titularidad del responsable o se utilizan con licencia, quedando prohibida su reproducción, distribución, comunicación pública, transformación o modificación sin autorización expresa.',
            'Los documentos, avisos, calendarios, comentarios o contenidos subidos por los usuarios seguirán perteneciendo a sus respectivos titulares. El usuario concede a Konvenio la autorización necesaria para alojarlos, mostrarlos, procesarlos y ponerlos a disposición de los usuarios autorizados en el marco del servicio.'
          ]
        },
        {
          title: '10. Contenido Introducido por Usuarios',
          paragraphs: [
            'Konvenio puede permitir que gestores o usuarios introduzcan documentos, avisos, comentarios, calendarios, datos de empresa o información laboral.',
            'El titular no revisa necesariamente todo el contenido introducido por los usuarios y no será responsable de dicho contenido, salvo en los casos previstos por la legislación aplicable.',
            'El titular podrá retirar, bloquear o limitar el acceso a contenidos que considere ilícitos, abusivos, contrarios a estas condiciones o perjudiciales para el servicio o terceros.'
          ]
        },
        {
          title: '11. Enlaces a Terceros',
          paragraphs: [
            'Konvenio puede incluir enlaces a sitios web, documentos o servicios de terceros. El titular no se responsabiliza del contenido, disponibilidad, seguridad o políticas de dichos sitios externos.'
          ]
        },
        {
          title: '12. Protección de Datos',
          paragraphs: [
            'El tratamiento de datos personales realizado a través de Konvenio se regula en la Política de Privacidad, donde se informa sobre los datos tratados, finalidades, bases jurídicas, destinatarios, conservación y derechos de los usuarios.'
          ]
        },
        {
          title: '13. Cookies',
          paragraphs: [
            'Konvenio puede utilizar cookies, almacenamiento local y tecnologías similares. El uso de estas tecnologías se regula en la Política de Cookies.'
          ]
        },
        {
          title: '14. Modificaciones',
          paragraphs: [
            'El titular se reserva el derecho de modificar este Aviso Legal para adaptarlo a cambios legales, técnicos, funcionales u operativos. La versión actualizada será aplicable desde su publicación o desde la fecha que se indique.'
          ]
        },
        {
          title: '15. Legislación Aplicable y Jurisdicción',
          paragraphs: [
            'La relación entre el usuario y el titular se regirá por la legislación española vigente.',
            'Salvo que la normativa aplicable disponga otra cosa, cualquier controversia se someterá a los juzgados y tribunales competentes conforme a la legislación española.'
          ]
        },
        {
          title: '16. Contacto',
          paragraphs: [],
          items: [
            '<strong>Sitio web:</strong> <a href="https://konvenio.thevalkiria.com/" target="_blank" rel="noopener">https://konvenio.thevalkiria.com/</a>',
            '<strong>Email:</strong> <a href="mailto:valkiria.lab.apps@gmail.com">valkiria.lab.apps@gmail.com</a>',
            '<strong>Titular:</strong> Sorraylla Ramos'
          ]
        }
      ]
    }
  }
}
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #7b2ff7, #ffffff);
}

.hero {
  min-height: 100vh;
  padding: 40px 0;
}

.legal-card {
  border-radius: 10px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.25);
}

.legal-date {
  color: #6f6a80;
  font-size: 0.95rem;
  line-height: 1.5;
}

.legal-section {
  margin-top: 28px;
}

.legal-text h2 {
  color: #2f2c43;
  font-size: 1.25rem;
  font-weight: 800;
  margin-bottom: 12px;
}

.legal-text p,
.legal-list li {
  color: #444;
  line-height: 1.7;
}

.legal-text p {
  margin-bottom: 14px;
}

.legal-list {
  margin: 0;
  padding-left: 22px;
}

.legal-list li {
  margin-bottom: 10px;
}

.legal-text a {
  color: #4f46e5;
  font-weight: 600;
  text-decoration: none;
}

.legal-text a:hover {
  text-decoration: underline;
}
</style>
